import React, { useState } from "react";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "./AuthProvider";
import { logBitacora, BITACORA_ACTIONS } from "../utils/bitacora";
import { CATEGORIAS_COSTO, saldoAFavorDe } from "../utils/costos";
import {
  parseDateInputToTimestamp,
  formatCalendarDate,
  todayInputValue,
  calendarDayAnchor,
} from "../utils/dates";
import { fmtMXNInput, normalizeMoneyInput } from "../utils/money";
import { showAlert } from "../alertService";
import MetodoPagoField from "./shared/MetodoPagoField";
import { Badge } from "./badge";
import DialogoConfirmacion, { Resumen, Fila } from "./shared/DialogoConfirmacion";
import { TrashIcon as TrashOutlineIcon } from "@heroicons/react/24/outline";
import ComprobanteCell from "./shared/ComprobanteCell";
import {
  BanknotesIcon,
  TrashIcon,
  PlusIcon,
  ScaleIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const fmtMoney = (v) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n).toLocaleString("es-MX", {
    minimumFractionDigits: n % 1 !== 0 ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return (n < 0 ? "-$" : "$") + abs;
};

const emptyForm = {
  fecha: "",
  monto: "",
  categoria: "Otro",
  concepto: "",
  estado: "por_pagar",
  metodo: "",
  notas: "",
  respaldo: "",
};

// Costos del evento: captura, lista y margen por proveedor. El componente no
// se suscribe — EventPage le pasa los costos ya vivos, porque también los
// necesita para el resumen de utilidad de arriba.
// Tabla de margen por respaldo. Vive fuera de la columna de costos porque
// cruza el desglose completo del evento: a media pantalla se aprieta y la
// pregunta que contesta ("¿cuánto me deja cada quien?") es del evento entero,
// no del lado del gasto.
// Cómo se pagó un costo, bajo su badge: la fecha en que salió el dinero (que
// no es la fecha del costo) y el método. Responde «¿cómo pagué esto?» sin
// abrir nada.
const DetallePago = ({ costo, timeZone }) => {
  const fecha = formatCalendarDate(costo.fechaPago || costo.fecha, timeZone, { style: "short" });
  const partes = [fecha, costo.metodo].filter(Boolean);
  if (partes.length === 0) return null;
  return <div className="mt-0.5 text-xs text-zinc-500 whitespace-nowrap">{partes.join(" · ")}</div>;
};

export const MargenPorRespaldo = ({ margenes = [] }) => {
  if (!margenes.length) return null;
  return (
    <>
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <div
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: ACCENT + "1a" }}
            >
              <ScaleIcon className="h-4 w-4" style={{ color: ACCENT }} />
            </div>
            <span className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
              Margen por proveedor y recurso
            </span>
          </div>
          <p className="text-xs text-zinc-500 mb-4 ml-9">
            Lo cobrado son las líneas del desglose que salen de cada
            proveedor o recurso, sin IVA — el IVA que le cobras al cliente no
            es ingreso tuyo.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100 dark:border-zinc-700/40">
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">Proveedor o recurso</th>
                  <th className="px-3 py-2 text-right font-semibold text-zinc-600">Cobrado</th>
                  <th className="px-3 py-2 text-right font-semibold text-zinc-600">Costo</th>
                  <th className="px-3 py-2 text-right font-semibold text-zinc-600">Margen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {margenes.map((m) => (
                  <tr key={m.key || m.id}>
                    <td className="px-3 py-2 text-zinc-900 dark:text-zinc-100">
                      <span className="inline-flex items-center gap-1.5">
                        {m.nombre || m.proveedorNombre || m.id}
                        <Badge color={m.tipo === "proveedor" ? "zinc" : "blue"}>
                          {m.tipo === "proveedor" ? "Proveedor" : "Recurso"}
                        </Badge>
                      </span>
                      {m.lineas === 0 && (
                        <div className="text-xs text-zinc-400">
                          Marca una línea del desglose como que sale de aquí
                          para calcular su margen.
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-zinc-600">
                      {m.lineas > 0 ? fmtMoney(m.cobrado) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right text-red-600">
                      {fmtMoney(m.costoTotal ?? m.costo)}
                      {/* Cuánto de ese costo lo sabe el catálogo pero nadie lo
                          ha capturado todavía. Decirlo evita que el renglón
                          parezca capturado cuando no lo está. */}
                      {m.costoEstimado > 0 && (
                        <div className="text-xs font-normal text-zinc-400">
                          {m.costo > 0
                            ? `${fmtMoney(m.costoEstimado)} estimado`
                            : "estimado"}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {m.lineas > 0 ? (
                        <span
                          className={m.margen < 0 ? "text-red-600" : "text-emerald-600"}
                        >
                          {fmtMoney(m.margen)}
                          {m.margenPct !== null && (
                            <span className="ml-1 text-xs font-normal text-zinc-400">
                              {(m.margenPct * 100).toFixed(0)}%
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-zinc-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

    </>
  );
};

const EventCostosSection = ({
  venueId,
  eventId,
  eventNombre,
  costos = [],
  proveedoresAsignados = [],
  recursos = [],
  timeZone,
  metodosPago = [],
  archivosByLinked = {},
  onArchivosChanged = null,
  // Solo lectura (inversionista, P-FRA-13): la lista con montos, estado y
  // comprobantes, sin captura, sin cambiar estado y sin borrar.
  soloLectura = false,
}) => {
  const { userInfo, user } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [masDetalles, setMasDetalles] = useState(false);

  const inputClass =
    "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

  const ordenados = [...costos].sort((a, b) => {
    const av = a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha || 0);
    const bv = b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha || 0);
    return bv - av;
  });

  const log = (action, costoId, details) =>
    logBitacora(
      venueId,
      {
        action,
        entity: "costo",
        entityId: eventId,
        entityName: eventNombre || "",
        details: { ...details, costoId },
      },
      userInfo,
      user
    );

  const handleAdd = async () => {
    if (!venueId || !eventId) return;
    if (!form.fecha) {
      showAlert("Por favor ingresa la fecha del costo.", "error");
      return;
    }
    const monto = Number(normalizeMoneyInput(form.monto));
    if (!monto || isNaN(monto) || monto <= 0) {
      showAlert("Por favor ingresa un monto válido mayor a 0.", "error");
      return;
    }
    if (!form.concepto.trim()) {
      showAlert("Por favor describe el concepto del costo.", "error");
      return;
    }
    const fecha = parseDateInputToTimestamp(form.fecha, timeZone);
    if (!fecha) {
      showAlert("Fecha de costo inválida.", "error");
      return;
    }
    const proveedorId = form.respaldo.startsWith("proveedor:")
      ? form.respaldo.slice("proveedor:".length)
      : null;
    const recursoId = form.respaldo.startsWith("recurso:")
      ? form.respaldo.slice("recurso:".length)
      : null;
    const proveedor = proveedoresAsignados.find(
      (p) => p.proveedorId === proveedorId
    );
    const recurso = recursos.find((r) => r.id === recursoId);
    setSaving(true);
    try {
      // Si no abrió "Más detalles", la categoría se deduce del respaldo en
      // vez de caer todo en "Otro" y arruinar el desglose de Cuentas.
      const categoria =
        masDetalles || form.categoria !== "Otro"
          ? form.categoria
          : proveedor
          ? "Proveedores"
          : recurso
          ? "Insumos"
          : "Otro";
      const docRef = await addDoc(collection(db, "venues", venueId, "costos"), {
        eventId,
        concepto: form.concepto.trim(),
        categoria,
        proveedorId: proveedor?.proveedorId || null,
        proveedorNombre: proveedor?.proveedorNombre || null,
        conceptoId: recurso?.id || null,
        conceptoNombre: recurso?.nombre || null,
        monto,
        montoOriginal: null,
        fecha,
        estado: form.estado,
        // Un costo capturado como pagado salió el día que dice su fecha;
        // por pagar no tiene fecha de pago (FRA-342).
        fechaPago: form.estado === "pagado" ? fecha : null,
        origen: "manual",
        metodo: form.metodo.trim() || null,
        notas: form.notas.trim() || null,
        createdAt: serverTimestamp(),
        createdBy: user?.uid || null,
        updatedAt: null,
        updatedBy: null,
      });
      log(BITACORA_ACTIONS.COSTO_CREATE, docRef.id, {
        monto,
        categoria,
        concepto: form.concepto.trim(),
        estado: form.estado,
      });
      setForm(emptyForm);
      setMasDetalles(false);
      showAlert("Costo registrado", "success");
    } catch (err) {
      showAlert(`Error al registrar el costo. [${err.message}]`, "error");
    } finally {
      setSaving(false);
    }
  };

  const patch = async (costo, cambios, action, details) => {
    try {
      await updateDoc(doc(db, "venues", venueId, "costos", costo.id), {
        ...cambios,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
      });
      log(action, costo.id, details);
    } catch (err) {
      showAlert(`No se pudo actualizar el costo. [${err.message}]`, "error");
    }
  };

  // Marcar pagado pide la fecha en que salió el dinero, con hoy como
  // propuesta (FRA-342): pagaste al DJ el viernes y lo registras el lunes,
  // y el egreso tiene que caer en viernes. Volverlo por pagar limpia la fecha.
  //
  // El mismo editor sirve para un costo ya pagado: picarle a «Pagado» abre
  // su pago (fecha y método) para consultarlo o corregirlo. Antes el badge
  // regresaba el costo a pendiente al primer clic y la única forma de
  // corregir el método era dar la vuelta completa.
  const [pagando, setPagando] = useState(null); // { costoId, fecha, metodo, editando }
  const marcarPorPagar = (costo) => {
    setPagando(null);
    patch(costo, { estado: "por_pagar", fechaPago: null }, BITACORA_ACTIONS.COSTO_TOGGLE_ESTADO, {
      estado: "por_pagar",
      monto: Number(costo.monto) || 0,
      concepto: costo.concepto || "",
    });
  };
  // «YYYY-MM-DD» de la fecha de pago registrada, para el <input type="date">.
  const fechaPagoInput = (costo) => {
    const anchor = calendarDayAnchor(costo.fechaPago || costo.fecha, timeZone);
    return anchor
      ? `${anchor.getUTCFullYear()}-${String(anchor.getUTCMonth() + 1).padStart(2, "0")}-${String(
          anchor.getUTCDate()
        ).padStart(2, "0")}`
      : "";
  };
  const abrirPago = (costo) => {
    setPagando({
      costoId: costo.id,
      fecha: fechaPagoInput(costo) || todayInputValue(),
      metodo: costo.metodo || "",
      editando: true,
    });
  };
  // `editando`: corrección de un pago ya registrado — el método se escribe
  // tal cual, aunque venga vacío, para poder quitarlo. En bitácora queda
  // como edición con el antes y el después (no como cambio de estado, que no
  // lo hubo), y si nada cambió no se escribe nada.
  const marcarPagado = (costo, fechaInput, metodo = "", editando = false) => {
    const fechaPago = parseDateInputToTimestamp(fechaInput, timeZone);
    if (!fechaPago) {
      showAlert("Fecha de pago inválida.", "error");
      return;
    }
    setPagando(null);
    if (editando) {
      const fechaAntes = fechaPagoInput(costo);
      const metodoAntes = costo.metodo || "";
      if (fechaAntes === fechaInput && metodoAntes === (metodo || "")) return;
      const resumen = (f, m) =>
        [formatCalendarDate(parseDateInputToTimestamp(f, timeZone), timeZone, { style: "short" }) || f, m || "sin método"].join(" · ");
      patch(costo, { fechaPago, metodo: metodo || null }, BITACORA_ACTIONS.COSTO_EDIT, {
        monto: Number(costo.monto) || 0,
        concepto: costo.concepto || "",
        from: resumen(fechaAntes, metodoAntes),
        to: resumen(fechaInput, metodo),
      });
      return;
    }
    patch(
      costo,
      { estado: "pagado", fechaPago, ...(metodo ? { metodo } : {}) },
      BITACORA_ACTIONS.COSTO_TOGGLE_ESTADO,
      {
        estado: "pagado",
        monto: Number(costo.monto) || 0,
        concepto: costo.concepto || "",
        fechaPago: fechaInput,
        ...(metodo ? { metodo } : {}),
      }
    );
  };

  const confirmDelete = async () => {
    const costo = deleteCandidate;
    if (!costo) return;
    try {
      await deleteDoc(doc(db, "venues", venueId, "costos", costo.id));
      log(BITACORA_ACTIONS.COSTO_DELETE, costo.id, {
        monto: Number(costo.monto) || 0,
        concepto: costo.concepto || "",
      });
      showAlert("Costo eliminado", "success");
    } catch (err) {
      showAlert(`No se pudo eliminar el costo. [${err.message}]`, "error");
    } finally {
      setDeleteCandidate(null);
    }
  };

  const porPagar = ordenados
    .filter((c) => (c.estado || "por_pagar") === "por_pagar")
    .reduce((s, c) => s + (Number(c.monto) || 0), 0);

  return (
    <div className="space-y-6">
      {/* Captura */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <div
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: ACCENT + "1a" }}
          >
            <BanknotesIcon className="h-4 w-4" style={{ color: ACCENT }} />
          </div>
          <span className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            Costos del evento
          </span>
          {porPagar > 0 && (
            <span className="ml-auto">
              <Badge color="amber">{fmtMoney(porPagar)} por pagar</Badge>
            </span>
          )}
        </div>

        {/* Captura rápida: concepto, monto, fecha y si ya se pagó. Todo lo
            demás (categoría, respaldo, método, notas) es clasificación y va
            colapsado — teclear ocho campos para anotar un costo de $2,500
            hace que nadie los anote. */}
        {!soloLectura && (
        <div className="rounded-lg border border-zinc-100 dark:border-zinc-700/40 bg-zinc-50 dark:bg-zinc-800/30 p-4 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
            <div className="lg:col-span-2">
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Concepto
              </label>
              <input
                type="text"
                value={form.concepto}
                onChange={(e) => setForm({ ...form, concepto: e.target.value })}
                placeholder="Renta de mobiliario, horas extra…"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Monto
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">
                  $
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={fmtMXNInput(form.monto)}
                  onChange={(e) =>
                    setForm({ ...form, monto: normalizeMoneyInput(e.target.value) })
                  }
                  placeholder="0"
                  className={`${inputClass} pl-7`}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Fecha
              </label>
              <input
                type="date"
                value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <div className="flex rounded-lg border border-zinc-300 dark:border-zinc-600 overflow-hidden">
              {[
                { id: "por_pagar", label: "Por pagar" },
                { id: "pagado", label: "Pagado" },
              ].map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => setForm({ ...form, estado: e.id })}
                  className={`px-3 py-1.5 text-sm font-medium transition ${
                    form.estado === e.id
                      ? "text-white"
                      : "bg-white dark:bg-zinc-800 text-zinc-500 hover:bg-zinc-50"
                  }`}
                  style={form.estado === e.id ? { backgroundColor: ACCENT } : undefined}
                >
                  {e.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setMasDetalles((v) => !v)}
              className="text-xs font-medium text-zinc-500 hover:text-zinc-700"
            >
              {masDetalles ? "− Menos detalles" : "+ Más detalles"}
            </button>
            <button
              type="button"
              onClick={handleAdd}
              disabled={saving}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: ACCENT }}
            >
              <PlusIcon className="h-4 w-4" />
              {saving ? "Guardando…" : "Registrar costo"}
            </button>
          </div>

          {masDetalles && (
            <div className="mt-3 pt-3 border-t border-zinc-200 dark:border-zinc-700/40 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">
                  Categoría
                </label>
                <select
                  value={form.categoria}
                  onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                  className={inputClass}
                >
                  {CATEGORIAS_COSTO.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">
                  Proveedor o servicio
                </label>
                <select
                  value={form.respaldo}
                  onChange={(e) => setForm({ ...form, respaldo: e.target.value })}
                  className={inputClass}
                >
                  <option value="">Ninguno</option>
                  {proveedoresAsignados.length > 0 && (
                    <optgroup label="Proveedor asignado">
                      {proveedoresAsignados.map((p) => (
                        <option key={p.proveedorId} value={`proveedor:${p.proveedorId}`}>
                          {p.proveedorNombre}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {recursos.length > 0 && (
                    <optgroup label="Recurso propio">
                      {recursos.map((r) => (
                        <option key={r.id} value={`recurso:${r.id}`}>
                          {r.nombre}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">
                  Método
                </label>
                {metodosPago.length > 0 ? (
                  <select
                    value={form.metodo}
                    onChange={(e) => setForm({ ...form, metodo: e.target.value })}
                    className={inputClass}
                  >
                    <option value="">Sin método</option>
                    {metodosPago.map((m) => (
                      <option key={m.id || m.nombre} value={m.nombre}>
                        {m.nombre}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={form.metodo}
                    onChange={(e) => setForm({ ...form, metodo: e.target.value })}
                    className={inputClass}
                  />
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">
                  Notas
                </label>
                <input
                  type="text"
                  value={form.notas}
                  onChange={(e) => setForm({ ...form, notas: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>
          )}
        </div>
        )}

        {/* Lista */}
        {ordenados.length === 0 ? (
          <p className="text-sm text-zinc-400 italic">
            Todavía no hay costos capturados en este evento.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: "#dc26260d" }}>
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">Fecha</th>
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">Concepto</th>
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">Categoría</th>
                  <th className="px-3 py-2 text-right font-semibold text-zinc-600">Monto</th>
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">Estado</th>
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">Comprobante</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {ordenados.map((c) => {
                  const pagado = (c.estado || "por_pagar") === "pagado";
                  return (
                    <tr key={c.id}>
                      <td className="px-3 py-2 whitespace-nowrap text-zinc-600">
                        {formatCalendarDate(c.fecha, timeZone, { style: "short" }) || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-zinc-900 dark:text-zinc-100">
                          {c.concepto || "—"}
                        </div>
                        {(c.proveedorNombre || c.conceptoNombre) && (
                          <div className="text-xs text-zinc-500">
                            {c.proveedorNombre || c.conceptoNombre}
                            {c.origen === "proveedor_auto" && " · automático"}
                          </div>
                        )}
                        {c.notas && (
                          <div className="text-xs text-zinc-400">{c.notas}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-zinc-500">{c.categoria || "—"}</td>
                      <td className="px-3 py-2 text-right font-medium text-zinc-900 dark:text-zinc-100">
                        {fmtMoney(c.monto)}
                      </td>
                      <td className="px-3 py-2">
                        {soloLectura ? (
                          <div>
                            <Badge color={pagado ? "emerald" : "amber"}>
                              {pagado ? "Pagado" : "Por pagar"}
                            </Badge>
                            {pagado && <DetallePago costo={c} timeZone={timeZone} />}
                          </div>
                        ) : pagando?.costoId === c.id ? (
                          <span className="inline-flex items-center gap-1.5">
                            <input
                              type="date"
                              value={pagando.fecha}
                              onChange={(e) => setPagando({ ...pagando, fecha: e.target.value })}
                              className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-2 py-1 text-xs"
                              title="Fecha en que salió el dinero"
                            />
                            <MetodoPagoField
                              value={pagando.metodo}
                              onChange={(v) => setPagando({ ...pagando, metodo: v })}
                              metodosPago={metodosPago}
                              className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-2 py-1 text-xs"
                              vacio="Método"
                            />
                            <button
                              type="button"
                              onClick={() => marcarPagado(c, pagando.fecha, pagando.metodo, pagando.editando)}
                              className="px-2 py-1 rounded-lg text-xs font-semibold text-white transition hover:opacity-90"
                              style={{ backgroundColor: ACCENT }}
                            >
                              {pagando.editando ? "Guardar" : "Confirmar"}
                            </button>
                            {/* Con saldo a favor (evento cancelado) no se ofrece:
                                el pago saldría de Cuentas pero la devolución del
                                proveedor se quedaría, y la caja mostraría dinero
                                que no existe. */}
                            {pagando.editando && !saldoAFavorDe(c) && (
                              <button
                                type="button"
                                onClick={() => marcarPorPagar(c)}
                                className="text-xs text-zinc-500 hover:text-amber-600 px-1 whitespace-nowrap"
                              >
                                Regresar a pendiente
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setPagando(null)}
                              className="text-xs text-zinc-400 hover:text-zinc-600 px-1"
                            >
                              Cancelar
                            </button>
                          </span>
                        ) : pagado ? (
                          <button
                            type="button"
                            onClick={() => abrirPago(c)}
                            title="Ver o corregir el pago"
                            className="text-left"
                          >
                            <Badge color="emerald">Pagado</Badge>
                            <DetallePago costo={c} timeZone={timeZone} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setPagando({ costoId: c.id, fecha: todayInputValue(), metodo: c.metodo || "" })}
                            title="Marcar como pagado"
                          >
                            <Badge color="amber">Por pagar</Badge>
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <ComprobanteCell
                          venueId={venueId}
                          eventId={eventId}
                          eventNombre={eventNombre}
                          linkedKind="costo"
                          linkedId={c.id}
                          linkedLabel={`Comprobante de ${c.concepto || "egreso"}`}
                          archivo={archivosByLinked[`costo:${c.id}`]}
                          currentUser={user}
                          userInfo={userInfo}
                          onChanged={onArchivosChanged}
                          soloLectura={soloLectura}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        {!soloLectura && (
                        <button
                          type="button"
                          onClick={() => setDeleteCandidate(c)}
                          className="text-zinc-400 hover:text-red-500 p-1"
                          title="Eliminar costo"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DialogoConfirmacion
        open={!!deleteCandidate}
        onClose={() => setDeleteCandidate(null)}
        titulo="Eliminar costo"
        subtitulo={deleteCandidate?.concepto || ""}
        icono={TrashOutlineIcon}
        cancelarLabel="Cancelar"
        confirmar={{ label: "Eliminar costo", tono: "rojo", onClick: confirmDelete }}
      >
        <p>
          {(deleteCandidate?.estado || "por_pagar") === "pagado"
            ? "El costo desaparece del evento, de Movimientos y de los reportes: la utilidad del evento sube en ese monto. Si lo que quieres es dejarlo pendiente otra vez, haz clic en «Pagado» y usa «Regresar a pendiente»."
            : "El costo desaparece del evento y de Cuentas → Por pagar: deja de contar como compromiso."}{" "}
          Esta acción no se puede deshacer.
        </p>
        {deleteCandidate && (
          <Resumen>
            <Fila tono="rojo" monto={fmtMoney(deleteCandidate.monto)}>
              {deleteCandidate.concepto || "Costo"}
            </Fila>
            <Fila tono="zinc">
              {(deleteCandidate.estado || "por_pagar") === "pagado"
                ? `Pagado el ${formatCalendarDate(deleteCandidate.fechaPago || deleteCandidate.fecha, timeZone, { style: "short" }) || "—"}`
                : `Por pagar · causado el ${formatCalendarDate(deleteCandidate.fecha, timeZone, { style: "short" }) || "—"}`}
              {deleteCandidate.metodo ? ` · ${deleteCandidate.metodo}` : ""}
              {deleteCandidate.proveedorNombre ? ` · ${deleteCandidate.proveedorNombre}` : ""}
            </Fila>
            {deleteCandidate.origen === "cotizacion_auto" && (
              <Fila tono="ambar">
                Lo generó la cotización. Si el renglón sigue en el desglose, la siguiente edición lo vuelve a proponer.
              </Fila>
            )}
          </Resumen>
        )}
      </DialogoConfirmacion>
    </div>
  );
};

export default EventCostosSection;
